import { PERCEPTION_MODELS } from 'constants/routePaths';

import * as React from 'react';

import { useMutation } from '@apollo/client';
import CreatePerceptionModelProject from '@graphql/mutations/CreatePerceptionModelProject';
import {
  AllProjectNormSetsQuery,
  type NormSetListData,
  type ProductLookupRow,
} from '@graphql/queries/PerceptionModelQueries';
import { ArrowBack } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';

import AnchorPicker from './AnchorPicker';
import CandidateSection from './CandidateSection';
import NormSetSummary from './NormSetSummary';
import {
  DEFAULT_TOP_N,
  MIN_PEERS,
  buildNormSet,
  distanceToAnchors,
  evaluateGates,
  normSetStats,
} from './normSetUtils';
import NormSetView from './NormSetView';
import PerceptionDataExplorer from './PerceptionDataExplorer';
import PerceptionModelError from './PerceptionModelError';
import PerceptionModelPicker from './PerceptionModelPicker';
import { sensorySignature } from './tokenProfile';
import useNormSetWorkbench from './useNormSetWorkbench';
import usePerceptionModels from './usePerceptionModels';
import { PageHeader, PagePaper, PageTitle } from '../../styles/themeComponents';

/**
 * Norm set builder.
 *
 * Picks a perception model, then hands the anchors to useNormSetWorkbench for
 * ranking. Ranked candidates are included by default — the analyst removes
 * rather than builds up — and token search adds products the ranking never
 * reaches.
 *
 * Everything downstream is keyed to the chosen model, so changing it discards
 * every per-product decision rather than silently reinterpreting them against a
 * different universe.
 */

type Props = { producerId: number };

const NormSetBuilder: React.FC<Props> = ({ producerId }) => {
  const history = useHistory();
  const [name, setName] = React.useState('');
  const [anchorNames, setAnchorNames] = React.useState<string[]>([]);
  const [topN, setTopN] = React.useState<number>(DEFAULT_TOP_N);
  // The ranking is the answer, so the list starts hidden: opening it is a
  // deliberate act, not the first thing the screen asks for.
  const [editing, setEditing] = React.useState(false);
  const [excluded, setExcluded] = React.useState<Set<string>>(new Set());
  const [customPicked, setCustomPicked] = React.useState<Set<string>>(
    new Set(),
  );

  // Honours ?jobId= from the list page. Without it the builder would default to
  // the latest version and quietly override the model just chosen, and a norm
  // set built on a different model is not comparable with its neighbours.
  const { search } = useLocation();
  const requestedJob = Number(new URLSearchParams(search).get('jobId'));
  const [jobId, setJobId] = React.useState<number | undefined>(
    Number.isInteger(requestedJob) && requestedJob > 0
      ? requestedJob
      : undefined,
  );

  const {
    allJobs,
    models,
    loading: jobLoading,
    error: jobError,
  } = usePerceptionModels();
  // Newest first, as the query orders it: the default is the latest run, and
  // the picker says so when that is not the deployed one.
  const job = models.find((j) => j.id === jobId) ?? models[0];

  /**
   * Anchors, exclusions and custom picks are all product names resolved against
   * one model's universe. Carrying them across a model change would keep the
   * screen looking valid while every distance behind it had changed.
   */
  const onModelChange = (next: number) => {
    setJobId(next);
    setAnchorNames([]);
    setExcluded(new Set());
    setCustomPicked(new Set());
  };

  /** Changing anchors invalidates every per-product decision made so far. */
  const onAnchorsChange = (next: string[]) => {
    setAnchorNames(next);
    setExcluded(new Set());
    // Map picks are per-product decisions too. Kept across an anchor change
    // they would be scored against anchors the analyst never saw them under.
    setCustomPicked(new Set());
  };

  const {
    anchorRows,
    missingAnchors,
    ambiguousAnchors,
    candidates,
    lookupByName,
    anchorMean,
    attributes,
    anchorLoading,
    candidatesLoading,
  } = useNormSetWorkbench({
    jobId: job?.id,
    anchorNames,
    topN,
    producerId,
    extraNames: customPicked,
  });

  // Ranked candidates are included by default — the analyst removes rather than
  // builds up — plus anything picked off the map, which is opt-in.
  //
  // Anchors are not filtered out. A norm set is a heuristic for choosing
  // products, so a product may anchor the set it belongs to; it is scored at
  // distance 0 from itself and its measured distance from the other anchors.
  const included = React.useMemo(() => {
    const set = new Set(
      candidates.map((c) => c.product).filter((p) => !excluded.has(p)),
    );
    customPicked.forEach((p) => {
      if (!excluded.has(p)) set.add(p);
    });
    return set;
  }, [candidates, excluded, customPicked]);

  /**
   * Membership here is expressed as exclusions from the ranking, so a custom
   * pick cannot go through `toggle` — that would record the analyst's "add" as
   * an exclusion and the product would be filtered straight back out.
   */
  const toggleCustom = (product: string) => {
    if (included.has(product)) {
      setCustomPicked((prev) => {
        const next = new Set(prev);
        next.delete(product);
        return next;
      });
      setExcluded((prev) => new Set(prev).add(product));
      return;
    }
    // Nothing this model cannot place against the anchors — its distance would
    // be stored empty, and nothing downstream catches that.
    if (!distanceToAnchors(anchorRows, product)) return;
    setCustomPicked((prev) => new Set(prev).add(product));
    setExcluded((prev) => {
      const next = new Set(prev);
      next.delete(product);
      return next;
    });
  };

  /**
   * Select or clear every ranked candidate. Only the ranked ones: products
   * picked off the explorer are not in this table, so clearing it must not
   * silently drop them too.
   */
  const toggleAllCandidates = (checked: boolean) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      candidates.forEach((c) => {
        if (checked) next.delete(c.product);
        else next.add(c.product);
      });
      return next;
    });
  };

  const toggle = (product: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(product)) next.delete(product);
      else next.add(product);
      return next;
    });
  };

  const gates = evaluateGates(
    anchorRows,
    missingAnchors,
    included.size,
    MIN_PEERS,
  );

  const stats = React.useMemo(
    () => normSetStats(candidates, included),
    [candidates, included],
  );

  /**
   * The norm set as it would be saved.
   *
   * Built with the same call `onSave` makes, so what is inspected below is
   * byte-for-byte what gets written — including custom picks, which carry a
   * null distance when `distanceToAnchors` cannot score them.
   */
  const proposed = React.useMemo(
    () => buildNormSet(candidates, included, anchorRows),
    [candidates, included, anchorRows],
  );

  /** Lookup rows for the membership, in the order the members are listed. */
  const memberRows = React.useMemo(
    () =>
      proposed
        .map((m) => lookupByName.get(m.product))
        .filter((r): r is ProductLookupRow => !!r),
    [proposed, lookupByName],
  );

  const families = React.useMemo(
    () => sensorySignature(memberRows, anchorRows),
    [memberRows, anchorRows],
  );

  const hasAnchors = anchorRows.length > 0;
  /** Anchors resolved and the ranking settled — there is a proposal to show. */
  const ranked = hasAnchors && !candidatesLoading;

  const [save, { loading: saving, error: saveError }] = useMutation(
    CreatePerceptionModelProject,
    {
      // Push the new row into the list query so the sidebar and the landing
      // list show it without a refetch. The list is scoped by producerId, so
      // the cache read has to carry it — without the variable this misses and
      // the new norm set does not appear until a reload.
      update: (cache, { data }) => {
        const created =
          data?.createPerceptionModelProject?.perceptionModelProject;
        if (!created) return;
        const existing = cache.readQuery<NormSetListData>({
          query: AllProjectNormSetsQuery,
          variables: { producerId },
        });
        if (!existing?.allPerceptionModelProjects) return;
        cache.writeQuery({
          query: AllProjectNormSetsQuery,
          variables: { producerId },
          data: {
            allPerceptionModelProjects: {
              ...existing.allPerceptionModelProjects,
              nodes: [created, ...existing.allPerceptionModelProjects.nodes],
            },
          },
        });
      },
    },
  );

  const onSave = async () => {
    const anchors = anchorRows.map((r) => r.productName);
    const peers = buildNormSet(candidates, included, anchorRows);
    const res = await save({
      variables: {
        normSet: {
          name: name.trim(),
          perceptionModelJobId: job.id,
          producerId,
          anchors,
          normSet: peers,
        },
      },
    });
    const id =
      res.data?.createPerceptionModelProject?.perceptionModelProject?.id ??
      null;
    // Straight into it. Saving a norm set is never the last step — a
    // translation is — and a banner offering an "Open" link was a second click
    // asking whether you meant the thing you just did.
    if (id != null) history.push(`${PERCEPTION_MODELS}/${id}`);
  };

  if (jobLoading) return <PagePaper>Loading perception models…</PagePaper>;

  // A schema error means the tables are not exposed; reporting that as "no
  // deployed model" sends people hunting for a model that already exists.
  if (jobError) {
    return (
      <PagePaper>
        <PerceptionModelError error={jobError} />
      </PagePaper>
    );
  }

  if (!job) {
    return (
      <PagePaper>
        <Alert severity="warning">
          No perception model has exported any products yet
          {allJobs.length > 0 &&
            ` — ${allJobs.length} run${allJobs.length === 1 ? '' : 's'} exist but none produced a product lookup`}
          . Trigger 1 must finish before a norm set can be built.
        </Alert>
      </PagePaper>
    );
  }

  return (
    <PagePaper>
      {/* One level up. Every page in this flow answers "how do I get back"
          the same way — see design-plans/perception-models-routing-audit.md. */}
      <Button
        size="small"
        startIcon={<ArrowBack fontSize="small" />}
        onClick={() => history.push(PERCEPTION_MODELS)}
        sx={{ textTransform: 'none', mb: 1 }}
      >
        Norm sets
      </Button>

      <PageHeader>Norm set</PageHeader>
      <PageTitle>New norm set</PageTitle>

      <Stack spacing={2} sx={{ mt: 2 }}>
        <TextField
          label="Norm set name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />

        <PerceptionModelPicker
          jobs={models}
          hidden={allJobs.length - models.length}
          value={job.id}
          onChange={onModelChange}
        />

        <AnchorPicker
          producerId={producerId}
          jobId={job?.id}
          value={anchorNames}
          onChange={onAnchorsChange}
          missing={missingAnchors}
        />

        {ambiguousAnchors.length > 0 && (
          <Alert severity="warning">
            Product names are shared across producers. The model rows for{' '}
            {ambiguousAnchors.join(', ')} are not built from your workspace's
            product of that name, so this norm set may be anchored on someone
            else's product.
          </Alert>
        )}

        {ranked && (
          <NormSetSummary
            stats={stats}
            topN={topN}
            editing={editing}
            onToggleEditing={() => setEditing((v) => !v)}
            families={families}
          />
        )}

        {/* Looking. Always available, and fed by the save path itself — so the
            thing inspected here is the thing that gets written. Hidden while
            editing so only one map is mounted at a time. */}
        {ranked && !editing && (
          <NormSetView
            jobId={job.id}
            anchors={anchorRows.map((r) => r.productName)}
            members={proposed}
          />
        )}

        {/* Changing. One region, because the table and the map are two halves
            of the same act and were always shown together. */}
        {hasAnchors && editing && (
          <>
            <CandidateSection
              candidates={candidates}
              lookupByName={lookupByName}
              anchorMean={anchorMean}
              attributes={attributes}
              included={included}
              totalIncluded={included.size}
              onToggle={toggle}
              onToggleAll={toggleAllCandidates}
              topN={topN}
              onTopNChange={setTopN}
              loading={candidatesLoading}
            />

            <Box>
              <Typography gutterBottom>Perception data explorer</Typography>
              <PerceptionDataExplorer
                jobId={job.id}
                anchorRows={anchorRows}
                anchorMean={anchorMean}
                attributes={attributes}
                included={included}
                onToggle={toggleCustom}
              />
            </Box>
          </>
        )}

        {anchorLoading && <Typography>Loading anchors…</Typography>}

        {saveError && <Alert severity="error">{saveError.message}</Alert>}

        <Box>
          <Button
            variant="contained"
            disabled={!gates.passes || !name.trim() || saving}
            onClick={onSave}
          >
            {saving ? 'Saving…' : 'Save norm set'}
          </Button>
        </Box>
      </Stack>
    </PagePaper>
  );
};

export default NormSetBuilder;
